import { useId, useState } from "react";

import NotFoundPage from "components/error/NotFoundPage";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { getSearchQuery } from "components/SearchInput/helpers";
import useErrorHandle from "hooks/useErrorHandle";
import useURLParams from "hooks/useURLParams";
import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";

import { TEMPLATES_LIMIT, TEMPLATES_LIMIT_STORAGE_KEY } from "./constants";
import { showCreateTemplateDrawer } from "./CreateTemplateDrawer";
import TemplatesWorkbenchEmpty from "./Empty";
import TemplatesFeatureGateCallout from "./FeatureGate/Callout";
import FiltersLayout from "./FiltersLayout";
import TemplatesWorkbenchPageLayout from "./PageLayout";
import useTemplates from "./hooks/useTemplates";
import TemplatesTable from "./Table";

const TemplatesWorkbench = () => {
  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);
  const [itemsCount, setItemsCount] = useState<number>(0);

  const {
    initialLoading,
    error,
    templates,
    loadTemplates,
    showUpSell,
    endCursor,
    startCursor,
    noResults,
    isEmpty,
    loadingContent,
    predicates,
    paginationResetKey,
  } = useTemplates();

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadTemplates,
    endCursor,
    startCursor,
    visibleItemsCount: templates.length,
    initialLimit: TEMPLATES_LIMIT,
    allItemsCount: itemsCount,
    storageKey: TEMPLATES_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const handleOpenCreateTemplateDrawer = () => {
    showCreateTemplateDrawer();
  };

  const ErrorContent = useErrorHandle(error);

  const titleId = useId();

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return (
      <TemplatesWorkbenchPageLayout titleId={titleId} hideActions>
        <PageLayoutSkeleton />
      </TemplatesWorkbenchPageLayout>
    );
  }

  if (!showUpSell && noResults) {
    return <NotFoundPage />;
  }

  const hasNoResultsWithFiltering = searchInput.length > 0 || predicates.length > 0;

  return (
    <TemplatesWorkbenchPageLayout
      titleId={titleId}
      onCreate={handleOpenCreateTemplateDrawer}
      showUpSell={showUpSell}
    >
      <TemplatesFeatureGateCallout />
      <FiltersLayout
        predicates={predicates}
        itemsCount={itemsCount}
        setItemsCount={setItemsCount}
        titleId={titleId}
      >
        {isEmpty && (
          <TemplatesWorkbenchEmpty
            hasNoResults={hasNoResultsWithFiltering}
            onCreate={handleOpenCreateTemplateDrawer}
          />
        )}

        {templates.length > 0 && (
          <>
            <TemplatesTable
              templates={templates}
              loading={loadingContent}
              rowsPerPage={rowsPerPage}
            />

            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={itemsCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </FiltersLayout>
    </TemplatesWorkbenchPageLayout>
  );
};

export default TemplatesWorkbench;
